import React, { useState } from 'react';
import { User, Mail, Phone, Bell, Globe, LogOut, Shield, FileText, ChevronRight, HelpCircle, Camera, Edit2, Check, ChevronLeft, MessageCircle, Calendar, UserPlus, Building2, Target, Clock, X, Send, AlertCircle, Bug, Lightbulb, Settings, Palette } from 'lucide-react';
import { BottomNav } from '../components/BottomNav';
interface ProfilePageProps {
  onBack: () => void;
  onNetworkClick?: () => void;
  onInventoryClick?: () => void;
  onRMIClick?: () => void;
  onNotificationsClick?: () => void;
  onLogout?: () => void;
  onDesignSystemClick?: () => void;
}
export function ProfilePage({
  onBack,
  onNetworkClick,
  onInventoryClick,
  onRMIClick,
  onNotificationsClick,
  onLogout,
  onDesignSystemClick
}: ProfilePageProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [language, setLanguage] = useState('English');
  const [showSupportModal, setShowSupportModal] = useState(false);
  // Support form state
  const [issueType, setIssueType] = useState('');
  const [supportMessage, setSupportMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Notification settings state
  const [chatNotifications, setChatNotifications] = useState(true);
  const [activitiesNotifications, setActivitiesNotifications] = useState(true);
  const [leadNotifications, setLeadNotifications] = useState(true);
  const [accreditedRequests, setAccreditedRequests] = useState(true);
  const [newMatched, setNewMatched] = useState(true);
  const [repeatInterval, setRepeatInterval] = useState('15');
  // Mock user data
  const [user, setUser] = useState({
    name: 'Sarah Ahmed',
    email: 'sarah.ahmed@example.com',
    phone: '+20 123 456 7890',
    role: 'Senior Real Estate Agent',
    company: 'Magic Properties'
  });
  const handleSave = () => {
    setIsEditing(false);
    // In a real app, this would save to backend
  };
  const handleSubmitSupport = () => {
    if (!issueType || !supportMessage.trim()) {
      alert('Please select an issue type and enter your message');
      return;
    }
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setShowSupportModal(false);
      setIssueType('');
      setSupportMessage('');
      alert('Support request submitted successfully! Our team will get back to you soon.');
    }, 1500);
  };
  const issueTypes = [{
    value: 'technical',
    label: 'Technical Issue',
    icon: Bug,
    color: 'text-red-500',
    bgColor: 'bg-red-50'
  }, {
    value: 'account',
    label: 'Account & Billing',
    icon: User,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50'
  }, {
    value: 'feature',
    label: 'Feature Request',
    icon: Lightbulb,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-50'
  }, {
    value: 'general',
    label: 'General Inquiry',
    icon: HelpCircle,
    color: 'text-purple-500',
    bgColor: 'bg-purple-50'
  }, {
    value: 'settings',
    label: 'Settings & Configuration',
    icon: Settings,
    color: 'text-green-500',
    bgColor: 'bg-green-50'
  }, {
    value: 'other',
    label: 'Other',
    icon: MessageCircle,
    color: 'text-gray-500',
    bgColor: 'bg-gray-50'
  }];
  return <div className="min-h-screen bg-neutral-bg pb-24 font-sans w-full" data-id="element-2149">
      <div className="w-full mx-auto bg-neutral-bg min-h-screen relative" data-id="element-2150">
        {/* Header Section */}
        <div className="bg-gradient-to-br from-primary to-primary-dark px-4 pt-8 pb-16 rounded-b-[2.5rem] shadow-lg relative overflow-hidden" data-id="element-2151">
          {/* Decorative circles */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-16 -mt-16 blur-2xl" data-id="element-2152"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-10 -mb-10 blur-xl" data-id="element-2153"></div>

          <div className="relative z-10 flex flex-col items-center text-center" data-id="element-2154">
            <div className="relative mb-4" data-id="element-2155">
              <div className="w-24 h-24 rounded-full bg-white p-1 shadow-xl" data-id="element-2156">
                <div className="w-full h-full rounded-full bg-gradient-to-br from-secondary to-accent flex items-center justify-center text-white text-2xl font-bold" data-id="element-2157">
                  SA
                </div>
              </div>
              <button className="absolute bottom-0 right-0 p-2 bg-white rounded-full shadow-md text-primary hover:text-secondary transition-colors" data-id="element-2158">
                <Camera className="w-4 h-4" data-id="element-2159" />
              </button>
            </div>

            <h1 className="text-2xl font-bold text-white mb-1" data-id="element-2160">{user.name}</h1>
            <p className="text-white/80 text-sm mb-4" data-id="element-2161">{user.role}</p>

            <div className="flex gap-3" data-id="element-2162">
              <button onClick={() => setIsEditing(!isEditing)} className="px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-sm font-medium hover:bg-white/20 transition-colors flex items-center gap-2" data-id="element-2163">
                {isEditing ? <>
                    <Check className="w-4 h-4" data-id="element-2164" /> Done
                  </> : <>
                    <Edit2 className="w-4 h-4" data-id="element-2165" /> Edit Profile
                  </>}
              </button>
            </div>
          </div>
        </div>

        {/* Content Container */}
        <div className="px-4 -mt-8 relative z-20 space-y-6" data-id="element-2166">
          {/* Personal Information */}
          <section className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-id="element-2167">
            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2168">
              <User className="w-4 h-4" data-id="element-2169" /> Personal Information
            </h2>

            <div className="space-y-4" data-id="element-2170">
              <div className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100" data-id="element-2171">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-500 flex-shrink-0" data-id="element-2172">
                  <User className="w-5 h-5" data-id="element-2173" />
                </div>
                <div className="flex-1" data-id="element-2174">
                  <p className="text-xs text-gray-400 mb-0.5" data-id="element-2175">Full Name</p>
                  {isEditing ? <input type="text" value={user.name} onChange={e => setUser({
                  ...user,
                  name: e.target.value
                })} className="w-full p-1 border-b border-secondary focus:outline-none text-sm font-semibold text-neutral-text bg-transparent" data-id="element-2176" /> : <p className="text-sm font-semibold text-neutral-text" data-id="element-2177">
                      {user.name}
                    </p>}
                </div>
              </div>

              <div className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100" data-id="element-2178">
                <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center text-purple-500 flex-shrink-0" data-id="element-2179">
                  <Mail className="w-5 h-5" data-id="element-2180" />
                </div>
                <div className="flex-1" data-id="element-2181">
                  <p className="text-xs text-gray-400 mb-0.5" data-id="element-2182">Email Address</p>
                  {isEditing ? <input type="email" value={user.email} onChange={e => setUser({
                  ...user,
                  email: e.target.value
                })} className="w-full p-1 border-b border-secondary focus:outline-none text-sm font-semibold text-neutral-text bg-transparent" data-id="element-2183" /> : <p className="text-sm font-semibold text-neutral-text" data-id="element-2184">
                      {user.email}
                    </p>}
                </div>
              </div>

              <div className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100" data-id="element-2185">
                <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center text-green-500 flex-shrink-0" data-id="element-2186">
                  <Phone className="w-5 h-5" data-id="element-2187" />
                </div>
                <div className="flex-1" data-id="element-2188">
                  <p className="text-xs text-gray-400 mb-0.5" data-id="element-2189">Phone Number</p>
                  {isEditing ? <input type="tel" value={user.phone} onChange={e => setUser({
                  ...user,
                  phone: e.target.value
                })} className="w-full p-1 border-b border-secondary focus:outline-none text-sm font-semibold text-neutral-text bg-transparent" data-id="element-2190" /> : <p className="text-sm font-semibold text-neutral-text" data-id="element-2191">
                      {user.phone}
                    </p>}
                </div>
              </div>
            </div>
          </section>

          {/* Notification Settings */}
          <section className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-id="element-2192">
            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2193">
              <Bell className="w-4 h-4" data-id="element-2194" /> Notification Settings
            </h2>

            <div className="space-y-1" data-id="element-2195">
              {/* Chat Notifications */}
              <div className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-2196">
                <div className="flex items-center gap-4" data-id="element-2197">
                  <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-500" data-id="element-2198">
                    <MessageCircle className="w-5 h-5" data-id="element-2199" />
                  </div>
                  <div data-id="element-2200">
                    <p className="text-sm font-semibold text-neutral-text" data-id="element-2201">
                      Chat Notifications
                    </p>
                    <p className="text-xs text-gray-400" data-id="element-2202">
                      New messages and replies
                    </p>
                  </div>
                </div>
                <div onClick={() => setChatNotifications(!chatNotifications)} className={`w-12 h-6 rounded-full p-1 transition-colors duration-300 ease-in-out cursor-pointer ${chatNotifications ? 'bg-secondary' : 'bg-gray-200'}`} data-id="element-2203">
                  <div className={`w-4 h-4 bg-white rounded-full shadow-sm transform transition-transform duration-300 ${chatNotifications ? 'translate-x-6' : 'translate-x-0'}`} data-id="element-2204" />
                </div>
              </div>

              {/* Activities Notifications */}
              <div className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-2205">
                <div className="flex items-center gap-4" data-id="element-2206">
                  <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center text-purple-500" data-id="element-2207">
                    <Calendar className="w-5 h-5" data-id="element-2208" />
                  </div>
                  <div data-id="element-2209">
                    <p className="text-sm font-semibold text-neutral-text" data-id="element-2210">
                      Activities Notifications
                    </p>
                    <p className="text-xs text-gray-400" data-id="element-2211">
                      Meetings, calls, and site visits
                    </p>
                  </div>
                </div>
                <div onClick={() => setActivitiesNotifications(!activitiesNotifications)} className={`w-12 h-6 rounded-full p-1 transition-colors duration-300 ease-in-out cursor-pointer ${activitiesNotifications ? 'bg-secondary' : 'bg-gray-200'}`} data-id="element-2212">
                  <div className={`w-4 h-4 bg-white rounded-full shadow-sm transform transition-transform duration-300 ${activitiesNotifications ? 'translate-x-6' : 'translate-x-0'}`} data-id="element-2213" />
                </div>
              </div>

              {/* Lead Notifications */}
              <div className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-2214">
                <div className="flex items-center gap-4" data-id="element-2215">
                  <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center text-green-500" data-id="element-2216">
                    <UserPlus className="w-5 h-5" data-id="element-2217" />
                  </div>
                  <div data-id="element-2218">
                    <p className="text-sm font-semibold text-neutral-text" data-id="element-2219">
                      Lead Notifications
                    </p>
                    <p className="text-xs text-gray-400" data-id="element-2220">
                      New leads and client updates
                    </p>
                  </div>
                </div>
                <div onClick={() => setLeadNotifications(!leadNotifications)} className={`w-12 h-6 rounded-full p-1 transition-colors duration-300 ease-in-out cursor-pointer ${leadNotifications ? 'bg-secondary' : 'bg-gray-200'}`} data-id="element-2221">
                  <div className={`w-4 h-4 bg-white rounded-full shadow-sm transform transition-transform duration-300 ${leadNotifications ? 'translate-x-6' : 'translate-x-0'}`} data-id="element-2222" />
                </div>
              </div>

              {/* Accredited Requests */}
              <div className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-2223">
                <div className="flex items-center gap-4" data-id="element-2224">
                  <div className="w-10 h-10 rounded-full bg-yellow-50 flex items-center justify-center text-yellow-600" data-id="element-2225">
                    <Building2 className="w-5 h-5" data-id="element-2226" />
                  </div>
                  <div data-id="element-2227">
                    <p className="text-sm font-semibold text-neutral-text" data-id="element-2228">
                      Accredited Requests
                    </p>
                    <p className="text-xs text-gray-400" data-id="element-2229">
                      Developer partnership requests
                    </p>
                  </div>
                </div>
                <div onClick={() => setAccreditedRequests(!accreditedRequests)} className={`w-12 h-6 rounded-full p-1 transition-colors duration-300 ease-in-out cursor-pointer ${accreditedRequests ? 'bg-secondary' : 'bg-gray-200'}`} data-id="element-2230">
                  <div className={`w-4 h-4 bg-white rounded-full shadow-sm transform transition-transform duration-300 ${accreditedRequests ? 'translate-x-6' : 'translate-x-0'}`} data-id="element-2231" />
                </div>
              </div>

              {/* New Matched */}
              <div className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-2232">
                <div className="flex items-center gap-4" data-id="element-2233">
                  <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center text-pink-500" data-id="element-2234">
                    <Target className="w-5 h-5" data-id="element-2235" />
                  </div>
                  <div data-id="element-2236">
                    <p className="text-sm font-semibold text-neutral-text" data-id="element-2237">
                      New Matched Properties
                    </p>
                    <p className="text-xs text-gray-400" data-id="element-2238">
                      Properties matching client criteria
                    </p>
                  </div>
                </div>
                <div onClick={() => setNewMatched(!newMatched)} className={`w-12 h-6 rounded-full p-1 transition-colors duration-300 ease-in-out cursor-pointer ${newMatched ? 'bg-secondary' : 'bg-gray-200'}`} data-id="element-2239">
                  <div className={`w-4 h-4 bg-white rounded-full shadow-sm transform transition-transform duration-300 ${newMatched ? 'translate-x-6' : 'translate-x-0'}`} data-id="element-2240" />
                </div>
              </div>

              {/* Repeat Notification Interval */}
              <div className="p-3 rounded-xl bg-gray-50 border border-gray-100 mt-4" data-id="element-2241">
                <div className="flex items-center gap-3 mb-3" data-id="element-2242">
                  <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-500" data-id="element-2243">
                    <Clock className="w-5 h-5" data-id="element-2244" />
                  </div>
                  <div className="flex-1" data-id="element-2245">
                    <p className="text-sm font-semibold text-neutral-text" data-id="element-2246">
                      Repeat Notifications
                    </p>
                    <p className="text-xs text-gray-400" data-id="element-2247">Remind me every</p>
                  </div>
                </div>
                <select value={repeatInterval} onChange={e => setRepeatInterval(e.target.value)} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-neutral-text focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-2248">
                  <option value="5" data-id="element-2249">5 minutes</option>
                  <option value="10" data-id="element-2250">10 minutes</option>
                  <option value="15" data-id="element-2251">15 minutes</option>
                  <option value="30" data-id="element-2252">30 minutes</option>
                  <option value="60" data-id="element-2253">1 hour</option>
                  <option value="120" data-id="element-2254">2 hours</option>
                  <option value="never" data-id="element-2255">Never repeat</option>
                </select>
              </div>
            </div>
          </section>

          {/* App Settings */}
          <section className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-id="element-2256">
            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2257">
              <Globe className="w-4 h-4" data-id="element-2258" /> App Settings
            </h2>

            <div className="space-y-1" data-id="element-2259">
              {/* Language Selector */}
              <div className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer group" data-id="element-2260">
                <div className="flex items-center gap-4" data-id="element-2261">
                  <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-500" data-id="element-2262">
                    <Globe className="w-5 h-5" data-id="element-2263" />
                  </div>
                  <div data-id="element-2264">
                    <p className="text-sm font-semibold text-neutral-text" data-id="element-2265">
                      Language
                    </p>
                    <p className="text-xs text-gray-400" data-id="element-2266">Change app language</p>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-2267">
                  <select value={language} onChange={e => setLanguage(e.target.value)} className="text-sm font-medium text-gray-600 bg-transparent border-none focus:ring-0 cursor-pointer text-right" data-id="element-2268">
                    <option value="English" data-id="element-2269">English</option>
                    <option value="Arabic" data-id="element-2270">العربية</option>
                    <option value="French" data-id="element-2271">Français</option>
                  </select>
                </div>
              </div>
            </div>
          </section>

          {/* Quick Links */}
          <section className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-id="element-2272">
            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2273">
              <HelpCircle className="w-4 h-4" data-id="element-2274" /> Support & Links
            </h2>

            <div className="space-y-1" data-id="element-2275">
              <button onClick={onNotificationsClick} className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors group" data-id="element-2276">
                <div className="flex items-center gap-4" data-id="element-2277">
                  <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center text-teal-500" data-id="element-2278">
                    <Bell className="w-5 h-5" data-id="element-2279" />
                  </div>
                  <div className="text-left" data-id="element-2280">
                    <p className="text-sm font-semibold text-neutral-text" data-id="element-2281">
                      Notifications Center
                    </p>
                    <p className="text-xs text-gray-400" data-id="element-2282">
                      View all your alerts
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gray-500 transition-colors" data-id="element-2283" />
              </button>

              {onDesignSystemClick && <button onClick={onDesignSystemClick} className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors group" data-id="element-2284">
                  <div className="flex items-center gap-4" data-id="element-2285">
                    <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center text-purple-500" data-id="element-2286">
                      <Palette className="w-5 h-5" data-id="element-2287" />
                    </div>
                    <div className="text-left" data-id="element-2288">
                      <p className="text-sm font-semibold text-neutral-text" data-id="element-2289">
                        Design System
                      </p>
                      <p className="text-xs text-gray-400" data-id="element-2290">
                        View component library
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gray-500 transition-colors" data-id="element-2291" />
                </button>}

              <button onClick={() => setShowSupportModal(true)} className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors group" data-id="element-2292">
                <div className="flex items-center gap-4" data-id="element-2293">
                  <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center text-pink-500" data-id="element-2294">
                    <HelpCircle className="w-5 h-5" data-id="element-2295" />
                  </div>
                  <div className="text-left" data-id="element-2296">
                    <p className="text-sm font-semibold text-neutral-text" data-id="element-2297">
                      Help & Support
                    </p>
                    <p className="text-xs text-gray-400" data-id="element-2298">
                      Contact support team
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gray-500 transition-colors" data-id="element-2299" />
              </button>
            </div>
          </section>

          {/* Logout Button */}
          <button onClick={onLogout} className="w-full bg-red-50 hover:bg-red-100 text-red-600 font-semibold py-4 rounded-xl transition-colors flex items-center justify-center gap-2 border border-red-100" data-id="element-2300">
            <LogOut className="w-5 h-5" data-id="element-2301" />
            Log Out
          </button>

          {/* Legal Links Footer */}
          <div className="flex items-center justify-center gap-6 py-4 text-gray-400" data-id="element-2302">
            <button className="flex items-center gap-1.5 text-xs hover:text-secondary transition-colors" data-id="element-2303">
              <Shield className="w-3.5 h-3.5" data-id="element-2304" />
              Privacy Policy
            </button>
            <div className="w-1 h-1 rounded-full bg-gray-300" data-id="element-2305"></div>
            <button className="flex items-center gap-1.5 text-xs hover:text-secondary transition-colors" data-id="element-2306">
              <FileText className="w-3.5 h-3.5" data-id="element-2307" />
              Terms & Conditions
            </button>
          </div>

          <div className="text-center pb-4" data-id="element-2308">
            <p className="text-[10px] text-gray-300" data-id="element-2309">
              Version 2.4.0 (Build 1024)
            </p>
          </div>
        </div>

        {/* Help & Support Modal */}
        {showSupportModal && <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-id="element-2310">
            <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl flex flex-col max-h-[90vh]" data-id="element-2311">
              {/* Header */}
              <div className="flex-shrink-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-3xl sm:rounded-t-2xl" data-id="element-2312">
                <div className="flex items-center gap-3" data-id="element-2313">
                  <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center text-pink-500" data-id="element-2314">
                    <HelpCircle className="w-5 h-5" data-id="element-2315" />
                  </div>
                  <h2 className="text-xl font-bold text-neutral-text" data-id="element-2316">
                    Help & Support
                  </h2>
                </div>
                <button onClick={() => setShowSupportModal(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-neutral-bg rounded-full transition-colors" data-id="element-2317">
                  <X className="w-5 h-5" data-id="element-2318" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-2319">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3" data-id="element-2320">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-2321" />
                  <div data-id="element-2322">
                    <p className="text-sm font-semibold text-blue-900 mb-1" data-id="element-2323">
                      Need help?
                    </p>
                    <p className="text-xs text-blue-700" data-id="element-2324">
                      Our support team typically responds within 24 hours. For
                      urgent issues, please call us directly.
                    </p>
                  </div>
                </div>

                {/* Issue Type Selection */}
                <div data-id="element-2325">
                  <label className="block text-sm font-bold text-neutral-text mb-3" data-id="element-2326">
                    What can we help you with? *
                  </label>
                  <div className="grid grid-cols-2 gap-3" data-id="element-2327">
                    {issueTypes.map(type => {
                  const Icon = type.icon;
                  return <button key={type.value} onClick={() => setIssueType(type.value)} className={`p-4 rounded-xl border-2 transition-all text-left ${issueType === type.value ? 'border-secondary bg-secondary/5' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2328">
                          <div className={`w-10 h-10 rounded-full ${type.bgColor} flex items-center justify-center mb-2`} data-id="element-2329">
                            <Icon className={`w-5 h-5 ${type.color}`} data-id="element-2330" />
                          </div>
                          <p className="text-sm font-semibold text-neutral-text" data-id="element-2331">
                            {type.label}
                          </p>
                        </button>;
                })}
                  </div>
                </div>

                {/* Message */}
                <div data-id="element-2332">
                  <label className="block text-sm font-bold text-neutral-text mb-2" data-id="element-2333">
                    Describe your issue *
                  </label>
                  <textarea value={supportMessage} onChange={e => setSupportMessage(e.target.value)} placeholder="Please provide as much detail as possible so we can help you better..." rows={6} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary resize-none text-sm" data-id="element-2334" />
                  <p className="text-xs text-gray-400 mt-2" data-id="element-2335">
                    {supportMessage.length} / 1000 characters
                  </p>
                </div>

                {/* Contact Info */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-2" data-id="element-2336">
                  <p className="text-xs font-semibold text-gray-600 mb-2" data-id="element-2337">
                    We'll respond to:
                  </p>
                  <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-2338">
                    <Mail className="w-4 h-4 text-gray-400" data-id="element-2339" />
                    <span data-id="element-2340">{user.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-2341">
                    <Phone className="w-4 h-4 text-gray-400" data-id="element-2342" />
                    <span data-id="element-2343">{user.phone}</span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 p-6 border-t border-gray-100" data-id="element-2344">
                <div className="flex gap-3" data-id="element-2345">
                  <button onClick={() => setShowSupportModal(false)} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 rounded-xl transition-colors" data-id="element-2346">
                    Cancel
                  </button>
                  <button onClick={handleSubmitSupport} disabled={!issueType || !supportMessage.trim() || isSubmitting} className="flex-1 bg-secondary hover:bg-secondary-dark text-white font-bold py-3 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-2347">
                    {isSubmitting ? <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-2348" />
                        Sending...
                      </> : <>
                        <Send className="w-4 h-4" data-id="element-2349" />
                        Submit Request
                      </>}
                  </button>
                </div>
              </div>
            </div>
          </div>}

        <BottomNav activeNav="profile" onNetworkClick={onNetworkClick} onInventoryClick={onInventoryClick} onRMIClick={onRMIClick} onNotificationsClick={onNotificationsClick} data-id="element-2350" />
      </div>
    </div>;
}